import React from 'react';

/**
 * Props for the AiSummary component.
 */
interface AiSummaryProps {
  summary: string | null;
  error: string | null;
  onDismiss: () => void;
}

/**
 * A component to display an AI-generated summary or an error message if the
 * summarization failed. It includes a dismiss button.
 * @param {AiSummaryProps} props - The props for the component.
 * @returns {React.ReactElement | null} - The rendered component, or null if there is no summary or error.
 */
const AiSummary: React.FC<AiSummaryProps> = ({ summary, error, onDismiss }) => {
  // Do not render the component if there is neither a summary nor an error to display.
  if (!summary && !error) {
    return null;
  }

  const isError = !!error;
  const content = error || summary;
  
  // Dynamically set CSS classes based on whether it's an error or a summary.
  const containerClasses = `
    mt-4 p-4 rounded-lg border flex items-start gap-4 transition-all duration-300 animate-fade-in-down
    ${isError ? 'bg-red-50 border-red-300' : 'bg-indigo-50 border-indigo-200'}
  `;
  const iconClasses = `
    flex-shrink-0 h-6 w-6
    ${isError ? 'text-red-500' : 'text-indigo-500'}
  `;
  const textClasses = `
    flex-grow text-sm
    ${isError ? 'text-red-800' : 'text-indigo-900'}
  `;

  return (
    <div className={containerClasses} role="alert">
      <div className={iconClasses}>
        {isError ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        )}
      </div>
      <div className={textClasses}>
        <h4 className="font-bold mb-1">{isError ? 'Summarization Failed' : 'AI Summary'}</h4>
        <p className="whitespace-pre-wrap">{content}</p>
      </div>
      <button 
        onClick={onDismiss} 
        className={`-mt-2 -mr-2 p-1 rounded-full hover:bg-black/10 transition-colors focus:outline-none ${isError ? 'text-red-500' : 'text-indigo-500'}`}
        aria-label="Dismiss summary"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default AiSummary;